import React, { useState, useCallback, useRef, Fragment } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ClipLoader } from 'react-spinners';
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { useModal } from '../../context/ModalContext';
import { useAlert } from '../../context/AlertContext';
import { login, resendConfirmation } from '../../redux/actions/auth';
import { validateEmail } from 'utils/emailValidation';

import ForgotPassword from 'components/ForgotPassword';
import EmailConfirmation from 'components/EmailConfirmation';
import { BrandHeader, LoginField, ErrorBanner } from './parts';

/* ============================================================
   <MainLoginView> — photo-top sign-in form. Brand-led header
   strip, then the form below. The "Forgot password?" affordance
   sits inline next to the password field's label.
   ============================================================ */
const MainLoginView = ({
  email,
  onEmailChange,
  password,
  onPasswordChange,
  errorMessage,
  isLoading,
  onForgot,
  onSubmit,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <BrandHeader />

      <div className="px-6 sm:px-9 pt-7 pb-1 flex flex-col gap-1.5">
        <h2 className="font-serif font-normal text-[28px] leading-[1.1] tracking-[-0.01em] text-ink m-0">
          {t('login.title')}
        </h2>
        <div className="text-[13.5px] leading-[1.5] text-muted-2">
          {t('login.subtitle', 'Sign in to keep planning your next market.')}
        </div>
      </div>

      <form className="px-6 sm:px-9 pt-3.5 pb-7 flex flex-col gap-3.5" onSubmit={onSubmit}>
        <LoginField
          label={t('login.email')}
          type="email"
          value={email}
          onChange={onEmailChange}
          placeholder="you@example.com"
          autoComplete="email"
        />
        <LoginField
          label={t('login.password')}
          type="password"
          value={password}
          onChange={onPasswordChange}
          placeholder="••••••••"
          autoComplete="current-password"
          forgotLabel={t('login.forgot')}
          onForgot={onForgot}
        />

        {errorMessage && <ErrorBanner>{errorMessage}</ErrorBanner>}

        <button
          type="submit"
          disabled={!email || !password || isLoading}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full
                     bg-brand text-ink border border-brand px-5 py-4 text-[15px] font-semibold
                     hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? <ClipLoader color="#1A1A1F" size={18} /> : t('login.confirm')}
        </button>
      </form>

      <div
        className="px-6 sm:px-9 pt-4 pb-7 flex flex-col gap-2.5 items-center text-center
                   border-t border-line bg-paper-2 text-[13px] text-muted-2"
      >
        <span>{t('login.no_account')}</span>
        <a
          href="/register"
          className="font-bold text-ink underline underline-offset-[3px] hover:text-yellow-04"
        >
          {t('login.create_account', 'Create an account')}
        </a>
      </div>
    </>
  );
};

const LoginModal = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const submittingRef = useRef(false);

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [unconfirmedEmail, setUnconfirmedEmail] = useState(null);

  const [isResending, setIsResending] = useState(false);
  const [showResendView, setShowResendView] = useState(false);

  const { isLoginModalOpen, hideLoginModal, consumePendingRedirect } =
    useModal();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const handleEmailChange = useCallback((value) => setEmail(value), []);
  const handlePasswordChange = useCallback((value) => setPassword(value), []);

  const closeDialog = () => {
    hideLoginModal();
    setErrorMessage('');
    setShowForgotPassword(false);
    setShowResendView(false);
    setIsLoading(false);
    setIsResending(false);
    setUnconfirmedEmail(null);
    setPassword('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    // Drop concurrent submits — protects against double-clicks, Enter+click,
    // and password-manager auto-submit racing the user's own click. Without
    // this, two requests can land out of order: a successful one closes the
    // modal while the slower failed one then overwrites the success alert
    // with a stale error.
    if (submittingRef.current) return;

    if (!validateEmail(email)) {
      setErrorMessage(t('login.invalid_email'));
      return;
    }
    if (password.length < 6) {
      setErrorMessage(t('login.short_password'));
      return;
    }

    submittingRef.current = true;
    setIsLoading(true);
    setErrorMessage('');
    setUnconfirmedEmail(null);

    try {
      await dispatch(login(email, password));

      const redirectTo = consumePendingRedirect();

      // Skip the success alert when we're about to navigate to a deep-link.
      // The destination view (e.g. EventManagerModal opening on the right
      // booking) is itself the confirmation, and stacking a HeadlessUI Dialog
      // on top of the alert traps focus and blocks the OK button.
      if (!redirectTo) {
        showAlert({
          type: 'success',
          title: t('alert_dialog.login.success_title'),
          message: t('alert_dialog.login.success_message'),
          primaryAction: { label: t('common.ok') },
        });
      }

      closeDialog();

      if (redirectTo) navigate(redirectTo);
    } catch (error) {
      const msg = error?.message || t('alert_dialog.login.error_message');
      setErrorMessage(msg);

      showAlert({
        type: 'error',
        title: t('alert_dialog.login.error_title'),
        message: msg,
        primaryAction: { label: t('common.ok') },
      });

      if (error?.canResend) {
        setUnconfirmedEmail(email);
        setShowResendView(true);
      }
    } finally {
      submittingRef.current = false;
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!unconfirmedEmail) return;

    setIsResending(true);
    try {
      await dispatch(resendConfirmation(unconfirmedEmail));
      showAlert({
        type: 'success',
        title: t('alert_dialog.login.resend_confirmation_success_title'),
        message: t('alert_dialog.login.resend_confirmation_success_message'),
        primaryAction: { label: t('common.ok') },
      });
      setUnconfirmedEmail(null);
      setShowResendView(false);
    } catch (err) {
      showAlert({
        type: 'error',
        title: t('alert_dialog.resend_confirmation.error_title'),
        message: err || t('alert_dialog.resend_confirmation.error_message'),
        primaryAction: { label: t('common.ok') },
      });
    } finally {
      setIsResending(false);
    }
  };

  // "Use another account" returns to an empty sign-in form.
  const handleUseAnotherAccount = () => {
    setShowResendView(false);
    setUnconfirmedEmail(null);
    setEmail('');
    setPassword('');
    setErrorMessage('');
  };

  return (
    <Transition appear show={isLoginModalOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeDialog}>
        {/* Scrim */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-ink/40 backdrop-blur-[2px]" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 sm:p-7">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-2 sm:scale-95"
            >
              <DialogPanel className="relative w-full max-w-[440px] bg-paper rounded-[22px] shadow-modal overflow-hidden flex flex-col max-h-[92vh]">
                {/* Shared pill close button */}
                <button
                  type="button"
                  onClick={closeDialog}
                  aria-label={t('common.close', 'Close')}
                  className="absolute top-4 right-4 z-20 grid place-items-center w-9 h-9
                             rounded-full bg-white/70 border border-line text-ink hover:bg-white transition"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>

                {showForgotPassword ? (
                  <ForgotPassword onBack={() => setShowForgotPassword(false)} />
                ) : showResendView ? (
                  <EmailConfirmation
                    isResending={isResending}
                    handleResend={handleResend}
                    onBack={handleUseAnotherAccount}
                    email={unconfirmedEmail}
                  />
                ) : (
                  <MainLoginView
                    email={email}
                    onEmailChange={handleEmailChange}
                    password={password}
                    onPasswordChange={handlePasswordChange}
                    errorMessage={errorMessage}
                    isLoading={isLoading}
                    onForgot={() => setShowForgotPassword(true)}
                    onSubmit={handleLogin}
                  />
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default LoginModal;
